import * as React from 'react';
import { styled } from '@mui/system';
import colors from '../../../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import ActionButton from 'Components/ActionButton';
import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps } from '@mui/material';
import { dialogActions, dialogContent, dialogTitle } from '../../../../../../config/theme/componentTheme';
import { actions, TCreationPromiseType } from 'Store/documents/actions';
import { actions as actionsForm } from 'Store/form';
import { useAppDispatch } from 'Store/index';
import { IFormContext } from 'Containers/FormContextHandler';
import ExaminationForm from 'Routes/Examination/Document/Form';
import { nowPartialDate } from 'neuro-utils';

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
  textTransform: 'uppercase',
});

const DialogPaper = (props: PaperProps): React.JSX.Element => <Paper square={true} {...props} />;

const ExaminationDialog = ({ codeName, editing, view, fm }: IOwnProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [editingData, setEditingData] = React.useState<Omit<IExamination, keyof IControlProps>>({});
  const dispatch = useAppDispatch();

  const isViewing = !editing && !!view?.viewing;

  // Create new examination document
  const addNewExamination = (): void => {
    const fetchID = async (): Promise<TCreationPromiseType> => actions.createDocument('examination', true)(dispatch);
    fetchID()
      .then((id: TCreationPromiseType) => {
        if (typeof id === 'object' && id._id) {
          actions.closeCommit(
            { id: id._id as string, name: 'examination' },
            editingData as TAnyObject & IControlProps,
          )(dispatch);
          dispatch(actionsForm.unsetEditing(id._id as string));
        }
      })
      .catch(() => {
        return;
      });
  };

  const getDefaultUnit = (): string | undefined => {
    switch (codeName) {
      case 'Li-BA42/40':
        return undefined;
      case 'P-NFL':
        return 'ng/l';
      default:
        return 'pg/ml';
    }
  };

  const units = getDefaultUnit();

  return (
    <React.Fragment>
      <div style={{ paddingRight: '2.6rem' }}>
        <Container justifyContent="flex-end" alignItems="center">
          <Item xs={8}>{fm('diagnosis.alzheimer.resultNotAvailable')}</Item>
          <Item xs={4}>
            {!isViewing && (
              <StyledButton style={{ textTransform: 'none' }} onClick={() => setDialogOpen(true)}>
                {`+ ${fm('general.new')}`}
              </StyledButton>
            )}
          </Item>
        </Container>
      </div>
      <Dialog open={dialogOpen} maxWidth="lg" fullWidth={true} PaperComponent={DialogPaper}>
        <DialogTitle style={dialogTitle}>{fm('examination.title')}</DialogTitle>
        <DialogContent style={{ ...dialogContent, overflowX: 'hidden' }}>
          <ExaminationForm
            formData={{
              onChange: (values: TOnChangeValues) => {
                setEditingData({ ...editingData, ...values });
              },
              document: editingData as IExamination,
            }}
            view={view}
            initData={{
              date: nowPartialDate(),
              codeName,
              ...(units && { units }),
            }}
          />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <StyledButton style={{ marginRight: '2rem' }} onClick={() => setDialogOpen(false)}>
            {fm('general.cancel')}
          </StyledButton>
          <ActionButton
            text="general.save"
            width={10}
            height={3}
            fontSize={16}
            onClick={() => {
              addNewExamination();
              setDialogOpen(false);
            }}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface IOwnProps extends Omit<IFormContext<IAlzheimer>, 'formData'> {
  codeName: IExamination['codeName'];
}

export default ExaminationDialog;
